import { useMemo } from 'react';

const DEFAULT_BANNER_IMAGE_URL =
  'https://res.cloudinary.com/highereducation/image/upload/v1739214820/BME/Voyager_Psych_Banner.png';

/**
 * Category matching below reproduces legacy UG/Grad ResultsBanner behavior: it keys off
 * display strings from the DCS step (plus common HTML entity spellings). That mirrors
 * what shipped before this refactor but does not scale as categories, encodings, or
 * taxonomies change. A durable follow-up is to branch on stable identifiers from flow
 * or tenant config (canonical slugs, option IDs, or explicit flags) instead of parsing
 * human-readable labels here.
 */

/** Normalizes common ampersand encodings from CMS/flow before category matching. */
function decodeCategoryAmpersands(raw: string): string {
  return raw
    .replace(/&amp;/gi, '&')
    .replace(/&#0*38;/gi, '&')
    .replace(/&#x0*26;/gi, '&');
}

/**
 * True if `phrase` matches in `lower` at a token boundary (not preceded by a
 * word char or hyphen), so e.g. `non-business & management` does not match
 * business. Avoids RegExp lookbehind for older Safari engines.
 */
function tokenPhraseMatch(lower: string, phrase: RegExp): boolean {
  const r = new RegExp(phrase.source, 'gi');
  let m: RegExpExecArray | null;
  while ((m = r.exec(lower)) !== null) {
    const i = m.index;
    if (i === 0 || !/[\w-]/.test(lower[i - 1])) return true;
  }
  return false;
}

/** Legacy display-string gate; see module note above for scalability limits. */
export function isBusinessManagementCategory(category?: string): boolean {
  if (!category || typeof category !== 'string') return false;
  const decoded = decodeCategoryAmpersands(category).trim();
  if (/^business-management$/i.test(decoded)) return true;
  return tokenPhraseMatch(
    decoded.toLowerCase(),
    /business\s*(&|and)\s*management/,
  );
}

/** Legacy display-string gate; see module note above for scalability limits. */
export function isPsychologyCategory(category?: string): boolean {
  if (!category || typeof category !== 'string') return false;
  const decoded = decodeCategoryAmpersands(category).trim();
  if (/^psychology-counseling$/i.test(decoded)) return true;
  return tokenPhraseMatch(
    decoded.toLowerCase(),
    /psychology\s*(&|and)\s*counseling/,
  );
}

export type ResultsBannerConfig = {
  header: string;
  desktopSubheader: string;
  mobileSubheader: string;
  listItem1: string;
  listItem2: string;
  listItem3: string;
  imageUrl: string;
  showImage: boolean;
};

type Args = {
  category?: string;
  firstName?: string;
};

/**
 * Derives the results-page banner config (header copy, value-prop bullets,
 * image visibility) from the visitor's category. The branching here mirrors
 * the legacy UG/Grad page variants and is the single source of truth so
 * callers do not duplicate the category match logic.
 */
export const useResultsBannerConfig = ({
  category,
  firstName,
}: Args): ResultsBannerConfig =>
  useMemo(() => {
    const isBusinessAndManagement = isBusinessManagementCategory(category);
    const isPsychology = isPsychologyCategory(category);

    const header =
      isBusinessAndManagement || isPsychology
        ? 'Welcome to your results!'
        : firstName
          ? `Hi, ${firstName}!`
          : 'Hi!';

    const desktopSubheader = !isBusinessAndManagement
      ? 'Select a program and connect with an advisor for more details about accreditation, faculty, internships, and more.'
      : 'Pick your favorite school, set up a call with an admissions administrator, and start your application — all in one place.';

    const mobileSubheader =
      'Connect with an advisor for info on accreditation, faculty and internships.';

    const listItem1 = isPsychology
      ? 'Compare psych programs.'
      : 'Compare online programs.';
    const listItem2 = 'Connect with your top choice.';
    const listItem3 = isPsychology
      ? 'Start your psych degree.'
      : 'Start your online degree.';

    return {
      header,
      desktopSubheader,
      mobileSubheader,
      listItem1,
      listItem2,
      listItem3,
      imageUrl: DEFAULT_BANNER_IMAGE_URL,
      showImage: !isBusinessAndManagement,
    };
  }, [category, firstName]);

/**
 * Companion derivation for the "exact results" list section header/subheader,
 * which the legacy variants gated on the same category match. Kept separate
 * so callers can opt in without paying for the banner derivation when only
 * the section copy is needed.
 */
export const useResultsListSectionCopy = ({
  category,
  subject,
}: {
  category?: string;
  subject?: string;
}) =>
  useMemo(() => {
    const isBusinessAndManagement = isBusinessManagementCategory(category);
    return {
      header: isBusinessAndManagement ? 'More Matches For You' : '',
      subheader: isBusinessAndManagement
        ? `These schools match your interests in ${subject}.`
        : '',
    };
  }, [category, subject]);
